import React, { useEffect, useState } from 'react';
import {
  fetchAppointmentsBySubmission,
  createAppointmentRecord,
  updateAppointmentRecord,
} from '../../services/appointments.service';
import { fetchSlots } from '../../services/slots.service';
import moment from 'moment-timezone';
import { sendAppointmentNotification } from '../../services/notifications.service';
import { getSubmissionById } from '../../services/submissions.service';
import { assignRandomVariant } from '../../services/userMessageVariant.service';

const SubmissionAppointments = ({ submissionId }) => {
  const [appointments, setAppointments] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [nextToken, setNextToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(null);
  const [mobileNumber, setMobileNumber] = useState('');
  const [submissionStatus, setSubmissionStatus] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const SLOTS_PER_PAGE = 3;

  const loadAppointments = async () => {
    if (!submissionId) return;
    const data = await fetchAppointmentsBySubmission(submissionId);
    setAppointments(data);
  };

  const sortSlots = (slots) => {
    return slots.sort((a, b) => {
      if (a.date < b.date) return -1;
      if (a.date > b.date) return 1;
      if (a.startTime < b.startTime) return -1;
      if (a.startTime > b.startTime) return 1;
      return 0;
    });
  };

  const loadAvailableSlots = async () => {
    setCurrentPage(0);
    let allSlots = [];
    let nextToken = null;
    try {
      do {
        const result = await fetchSlots({}, 50, nextToken);
        if (result && result.items && result.items.length > 0) {
          allSlots = [...allSlots, ...result.items];
        }
        nextToken = result.nextToken;
      } while (nextToken);
      allSlots = sortSlots(allSlots);
      setAvailableSlots(allSlots);
      setNextToken(null);
    } catch (err) {
      console.error('Failed to load slots:', err);
    }
  };

  const handleBook = async (slot) => {
    setBooking(slot.id);
    try {
        console.log("createAppointmentRecord payload:", {
            submissionId,
            slotId: slot.id,
            status: 'SCHEDULED',
            bookedAt: new Date().toISOString(),
          });        
      await createAppointmentRecord({
        submissionId,
        slotId: slot.id,
        status: 'SCHEDULED',
        bookedAt: new Date().toISOString(),
      });

      // Assign random variant track (creates or updates single entry per user)
      let variantTrack = 1; // default fallback
      try {
        const variantAssignment = await assignRandomVariant(submissionId);
        variantTrack = variantAssignment?.variantTrack || 1;
        console.log(`[INFO] Assigned random variant track ${variantTrack} for appointment`);
      } catch (variantError) {
        console.error('[WARN] Failed to assign variant, using default track 1:', variantError);
      }

      await sendAppointmentNotification({
        mobileNumber: mobileNumber,
        slotDate: slot.date,
        startTime: slot.startTime,
        variantTrack: variantTrack,
        submissionId: submissionId
      });

      await loadAppointments();
      await loadAvailableSlots();
    } catch (err) {
      console.error('Failed to book appointment:', err);
    } finally {
      setBooking(null);
    }
  };
  const handleStatusChange = async (id, status) => {
    await updateAppointmentRecord({ id, status });
    await loadAppointments();
    await loadAvailableSlots(); // refresh open slots after status change
  };
  
  const formatSlotTime = (slot) => {
    const start = moment.tz(`${slot.date}T${slot.startTime}`, 'America/New_York').format('h:mm A');
    const end = moment.tz(`${slot.date}T${slot.endTime}`, 'America/New_York').format('h:mm A');
    return `${slot.date} — ${start} to ${end} EST`;
  };  

  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(0, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(Math.ceil(availableSlots.length / SLOTS_PER_PAGE) - 1, prev + 1));
  };

  useEffect(() => {
    const loadSubmission = async () => {
      if (!submissionId) return;
      try {
        const submission = await getSubmissionById(submissionId);
        setMobileNumber(submission?.mobilenumber || '');
        setSubmissionStatus(submission?.Status || '');
      } catch (err) {
        console.error('Failed to load submission:', err);
      }
    };
  
    loadSubmission();
    loadAppointments();
    loadAvailableSlots();
  }, [submissionId]);
  
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-2">Existing Appointments</h3>
        {appointments.length === 0 ? (
          <p className="text-gray-500 text-sm">No appointments yet.</p>
        ) : (
        <ul className="space-y-2">
          {appointments.map((appt) => (
            <li key={appt.id} className="border p-3 rounded bg-white shadow-sm">
              <div className="text-sm">
                <p>
                  <strong>Time:</strong>{' '}
                  {moment.tz(`${appt.slot?.date}T${appt.slot?.startTime}`, 'America/New_York').format('h:mm A')} -{' '}
                  {moment.tz(`${appt.slot?.date}T${appt.slot?.endTime}`, 'America/New_York').format('h:mm A')} EST
                </p>
                <p><strong>Date:</strong> {appt.slot?.date}</p>
                <p><strong>Status:</strong> {appt.status}</p>
              </div>
              <div className="mt-2">
                <select
                  value={appt.status}
                  onChange={(e) => handleStatusChange(appt.id, e.target.value)}
                  className="border p-1 rounded"
                >
                  <option value="SCHEDULED">Scheduled</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="CANCELED">Canceled</option>
                  <option value="NO_SHOW">No Show</option>
                </select>
              </div>
            </li>
          ))}
        </ul>
        )}
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-2">Book a New Appointment</h3>
        {availableSlots.length === 0 ? (
          <p className="text-gray-500 text-sm">No available slots in the next 2 weeks.</p>
        ) : (
          <>
            <ul className="space-y-2">
              {availableSlots
                .slice(currentPage * SLOTS_PER_PAGE, (currentPage + 1) * SLOTS_PER_PAGE)
                .map((slot) => (
                  <li key={slot.id} className="border p-3 rounded bg-white shadow-sm flex justify-between items-center">
                    <div>
                      <div>{formatSlotTime(slot)}</div>
                    </div>
                    <button
                      disabled={booking === slot.id}
                      onClick={() => handleBook(slot)}
                      className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 disabled:opacity-50"
                    >
                      {booking === slot.id ? 'Booking...' : 'Book'}
                    </button>
                  </li>
                ))}
            </ul>
            <div className="flex justify-between w-full mt-4">
              <button
                onClick={handlePreviousPage}
                disabled={currentPage === 0}
                className="bg-gray-100 text-gray-700 text-sm font-medium px-4 py-2 rounded hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={handleNextPage}
                disabled={(currentPage + 1) * SLOTS_PER_PAGE >= availableSlots.length}
                className="bg-gray-100 text-gray-700 text-sm font-medium px-4 py-2 rounded hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SubmissionAppointments;
